import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

const VIDEO_HD = "/media/monplaisir-hero.mp4";
const VIDEO_SM = "/media/monplaisir-hero-sm.mp4";
const POSTER = "/media/monplaisir-poster.jpg";

const CHAPTERS = [
    {
        chapter: "I - The Drive",
        headline: ["When was the last time", "something was ", "your first time?"],
        sub: "Bespoke travel and lifestyle services. Discreet. Considered. Without limit.",
    },
    {
        chapter: "II - The Threshold",
        headline: ["Chauffeurs driven", "by ", "excellence."],
        sub: "Quietly, faultlessly, on time.",
    },
    {
        chapter: "III - The Sky",
        headline: ["Wheels up.", "Quietly. ", "Anywhere."],
        sub: "Private aviation at short notice. Jets, helicopters, airside reception.",
    },
    {
        chapter: "IV - The Sea",
        headline: ["The sea,", "on ", "your terms."],
        sub: "Yachts on charter, the Mediterranean to the Caribbean, provisioned, crewed.",
    },
];

const ROTATE_MS = 5500;

function pickSource() {
    if (typeof navigator === "undefined") return { src: VIDEO_HD, skip: false };
    const c =
        navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (c?.saveData) return { src: null, skip: true };
    if (c?.effectiveType && /^(slow-2g|2g)$/.test(c.effectiveType))
        return { src: null, skip: true };
    if (c?.effectiveType === "3g") return { src: VIDEO_SM, skip: false };
    const small = typeof window !== "undefined" && window.innerWidth < 768;
    return { src: small ? VIDEO_SM : VIDEO_HD, skip: false };
}

export default function Hero() {
    const videoRef = useRef(null);
    const blurVideoRef = useRef(null);
    const sectionRef = useRef(null);
    const [loaded, setLoaded] = useState(false);
    const [muted, setMuted] = useState(true);
    const [{ src, skip }] = useState(() => pickSource());
    const [i, setI] = useState(0);

    // Auto-rotate text — ALWAYS. Independent of hover, sound, or interaction.
    useEffect(() => {
        const t = setInterval(
            () => setI((p) => (p + 1) % CHAPTERS.length),
            ROTATE_MS
        );
        return () => clearInterval(t);
    }, []);

    // Pause/resume the video when off-screen
    useEffect(() => {
        if (skip) return;
        const el = sectionRef.current;
        if (!el) return;
        const io = new IntersectionObserver(
            ([entry]) => {
                [videoRef.current, blurVideoRef.current].forEach((v) => {
                    if (!v) return;
                    if (entry.isIntersecting) v.play().catch(() => {});
                    else v.pause();
                });
            },
            { threshold: 0.15 }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [skip]);

    // Keep the blurred fill video in sync with the main one
    useEffect(() => {
        const main = videoRef.current;
        const blur = blurVideoRef.current;
        if (!main || !blur) return;
        const sync = () => {
            if (Math.abs(blur.currentTime - main.currentTime) > 0.4) {
                try {
                    blur.currentTime = main.currentTime;
                } catch {}
            }
        };
        const id = setInterval(sync, 1500);
        return () => clearInterval(id);
    }, [skip]);

    // Force play on mount (belt & braces — some browsers don't honour autoplay attr alone)
    useEffect(() => {
        if (skip) return;
        const v = videoRef.current;
        const b = blurVideoRef.current;
        const tryPlay = (el) => el && el.play().catch(() => {});
        tryPlay(v);
        tryPlay(b);
    }, [skip]);

    const toggleSound = () => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = !v.muted;
        setMuted(v.muted);
        if (!v.muted) v.play().catch(() => {});
    };

    const c = CHAPTERS[i];

    return (
        <section
            id="top"
            ref={sectionRef}
            data-testid="hero-section"
            className="relative h-[100svh] min-h-[680px] w-full overflow-hidden bg-[#0a0a0a] select-none"
        >
            {/* Poster */}
            <img
                src={POSTER}
                alt="Monplaisir"
                aria-hidden="true"
                className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[1200ms] ${
                    loaded && !skip ? "opacity-0" : "opacity-100"
                }`}
            />

            {!skip && (
                <>
                    {/* Blurred fill — covers desktop wings so the main portrait video sits unblurred and uncropped */}
                    <video
                        ref={blurVideoRef}
                        src={src}
                        poster={POSTER}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="auto"
                        aria-hidden="true"
                        className="absolute inset-0 z-0 h-full w-full object-cover scale-110 blur-3xl opacity-60 pointer-events-none hidden md:block"
                    />
                    {/* Main video — full cover on mobile (portrait fits portrait), contained on desktop so the whole frame is visible without zoom */}
                    <motion.video
                        ref={videoRef}
                        src={src}
                        poster={POSTER}
                        autoPlay
                        muted
                        loop
                        playsInline
                        preload="auto"
                        data-testid="hero-video"
                        onCanPlay={() => setLoaded(true)}
                        onLoadedData={() => setLoaded(true)}
                        initial={{ opacity: 0, scale: 1.04 }}
                        animate={{ opacity: loaded ? 1 : 0, scale: loaded ? 1 : 1.04 }}
                        transition={{ duration: 1.4, ease: [0.2, 0.7, 0.2, 1] }}
                        className="absolute inset-0 z-10 h-full w-full object-cover md:object-contain"
                    />
                </>
            )}

            {/* Cinematic overlays */}
            <div className="absolute inset-0 z-20 bg-gradient-to-r from-black/80 via-black/40 to-black/30 pointer-events-none" />
            <div className="absolute inset-0 z-20 bg-gradient-to-b from-black/30 via-transparent to-black/70 pointer-events-none" />

            {/* Click-to-advance overlay */}
            <button
                type="button"
                onClick={() => setI((p) => (p + 1) % CHAPTERS.length)}
                aria-label="Next chapter"
                data-testid="hero-advance-zone"
                className="absolute inset-0 z-30 cursor-pointer focus:outline-none"
            />

            {/* Foreground */}
            <div className="absolute inset-0 z-40 max-w-[1400px] mx-auto px-6 md:px-12 flex flex-col justify-between pt-28 pb-24 md:pb-32 pointer-events-none">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.4 }}
                    className="flex items-center gap-3 text-[#F9F8F6]/80"
                >
                    <span className="font-sans text-[10px] uppercase tracking-[0.4em]">
                        Est. London. Worldwide
                    </span>
                </motion.div>

                <div className="max-w-5xl">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 28 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -18 }}
                            transition={{ duration: 0.95, ease: [0.2, 0.7, 0.2, 1] }}
                        >
                            <div className="flex items-center gap-3 mb-6 text-[#E5C7A1]">
                                <span
                                    data-testid="hero-chapter"
                                    className="font-sans text-[10px] uppercase tracking-[0.45em]"
                                >
                                    {c.chapter}
                                </span>
                            </div>
                            <h1
                                data-testid="hero-headline"
                                className="font-serif text-[#F9F8F6] leading-[1.02] tracking-tight"
                                style={{ fontSize: "clamp(2.4rem, 6.8vw, 6.2rem)" }}
                            >
                                {c.headline[0]}
                                <br />
                                {c.headline[1]}
                                <span className="italic text-[#E5C7A1]">{c.headline[2]}</span>
                            </h1>
                            <p className="mt-8 max-w-xl font-sans text-[15px] md:text-[16px] leading-relaxed text-[#F9F8F6]/80">
                                {c.sub}
                            </p>
                        </motion.div>
                    </AnimatePresence>
                </div>

                <div className="flex items-end justify-end gap-6 text-[#F9F8F6] font-sans text-[10px] uppercase tracking-[0.35em]">
                    <a
                        href="#about"
                        data-testid="hero-scroll-cue"
                        className="pointer-events-auto group inline-flex items-center gap-3 border border-[#C5A880]/60 hover:border-[#E5C7A1] hover:bg-[#E5C7A1]/10 px-5 py-3 md:px-6 md:py-4 transition-all duration-500"
                    >
                        <span className="text-[11px] md:text-[12px] tracking-[0.35em] text-[#E5C7A1]">
                            Continue
                        </span>
                        <motion.span
                            initial={{ y: 0 }}
                            animate={{ y: 0 }}
                            className="text-[#E5C7A1]"
                        >
                            ↓
                        </motion.span>
                    </a>
                </div>
            </div>

            {/* Sound toggle */}
            {!skip && (
                <button
                    type="button"
                    onClick={toggleSound}
                    aria-label={muted ? "Unmute video" : "Mute video"}
                    data-testid="hero-sound-toggle"
                    className="absolute top-24 right-6 md:right-12 z-50 flex items-center justify-center w-11 h-11 border border-[#C5A880]/60 hover:border-[#E5C7A1] hover:bg-[#E5C7A1]/10 transition-all duration-500 backdrop-blur-sm"
                >
                    {muted ? (
                        <VolumeX size={16} className="text-[#E5C7A1]" />
                    ) : (
                        <Volume2 size={16} className="text-[#E5C7A1]" />
                    )}
                </button>
            )}
        </section>
    );
}
